import { useEffect, useRef, type RefObject } from "react";
import { useBodyScrollLock } from "./useBodyScrollLock.js";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Mount-order stack of the open overlays that own an Escape handler. Only the TOPMOST responds to Escape, so
// an ac-dialog stacked over a modal ("ac-dialog confirms stack over modals") closes just itself on
// the first Escape, not the modal behind it. Each mount pushes a unique token and pops it on unmount, so the
// last-mounted (visually topmost) overlay is always `stack[stack.length - 1]`.
const escapeStack: symbol[] = [];

/**
 * Whether a stacked overlay currently owns Escape. For a surface that handles its OWN Escape outside this stack
 * (the messages inbox): it must stand down while a dialog it opened is up, or one press closes both — the dialog
 * by this stack and the surface by its own listener (ART-264, a profile opened over a conversation).
 */
export function escapeOwnedByOverlay(): boolean {
  return escapeStack.length > 0;
}

/**
 * Dialog a11y for the `@cntrl/ui` overlay primitives — the one tested implementation `Modal`
 * and `AcDialog` both fold in (promoted from the `features/releases/Modal.tsx` precedent extracted at).
 * It: closes on Escape but ONLY for the topmost overlay (so stacked confirms unwind one layer at a time);
 * freezes body scroll while open (idempotent + nesting-safe — each layer restores the value it captured);
 * moves focus to the dialog panel itself on mount (not its first control — see the effect) and TRAPS Tab/Shift+Tab within it (so a
 * keyboard user can't reach the inert page behind the scrim, honouring `aria-modal`); and restores focus to
 * the opener on unmount (skipping a stale/`body` target, so a caller can re-home focus itself).
 *
 * `onClose` is read through a ref so the mount-order Escape stack stays stable even when the caller passes a
 * fresh closure each render — the topmost-ness is fixed at mount, but the latest `onClose` still fires.
 *
 * `enabled` (default true) is for an overlay that stays MOUNTED while closed — a "warm" sheet kept in the DOM
 * so opening is a CSS state flip with no mount cost (the More sheet). While disabled the hook is fully inert:
 * it takes no Escape-stack slot (so it can't swallow Escape from the overlay actually on screen), traps no
 * focus, and counts against no scroll lock — so a closed-but-mounted overlay behaves exactly like an
 * unmounted one. It becomes topmost only when it opens, which is the correct moment.
 */
export function useModalA11y(ref: RefObject<HTMLElement | null>, onClose: () => void, enabled = true) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Escape closes — but only when this overlay is the topmost open one.
  useEffect(() => {
    if (!enabled) return;
    const token = Symbol("modal");
    escapeStack.push(token);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && escapeStack[escapeStack.length - 1] === token) onCloseRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      const i = escapeStack.lastIndexOf(token);
      if (i >= 0) escapeStack.splice(i, 1);
    };
  }, [enabled]);

  // Freeze the page behind the overlay while open — extracted so a hand-rolled overlay that cannot adopt
  // this whole hook (the messages inbox, with its own two-layer Escape) shares the same ref-counted lock.
  useBodyScrollLock(enabled);

  // Focus in, TRAP Tab within the dialog, RESTORE focus to a real opener on unmount.
  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // Focus the PANEL, not its first control. A script `.focus()` on a button draws the `:focus-visible` ring
    // whenever nothing was focused before — always the case on iOS, where a tap never focuses its button — so
    // every sheet opened with a crimson outline around its first row. The panel is programmatically focusable
    // only (tabindex -1, never a Tab stop) and draws no outline; a screen reader still lands inside the dialog,
    // and the first Tab reaches the first control with its ring, which is the one time a ring is wanted.
    // A child that already took focus (an `autoFocus` field) keeps it.
    if (el && !el.contains(document.activeElement)) {
      if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
      el.style.outline = "none";
      el.focus({ preventScroll: true });
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !el) return;
      const f = el.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (f.length === 0) {
        e.preventDefault();
        return;
      }
      const first = f[0];
      const last = f[f.length - 1];
      // From the panel itself, Shift+Tab would leave the dialog; wrap it to the last control. (A plain Tab from
      // the panel already lands on the first control.)
      if (e.shiftKey && document.activeElement === el) {
        e.preventDefault();
        last.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    el?.addEventListener("keydown", onKey);
    return () => {
      el?.removeEventListener("keydown", onKey);
      // Restore only to a real, still-connected opener — never park focus on <body> (e.g. when the trigger was
      // a menu item that has since unmounted); the caller re-homes focus in that case.
      if (previouslyFocused && previouslyFocused !== document.body && previouslyFocused.isConnected) {
        previouslyFocused.focus?.();
      }
    };
  }, [ref, enabled]);
}
